export interface IntegrationFormData {
  auth_fields: Record<string, string>;
}

export type IntegrationStatus = 'active' | 'inactive' | 'pending' | 'failed' | 'connected' | 'disconnected' | 'syncing' | 'synced';

export type IntegrationClassification = 
  | 'llm'
  | 'project_management' 
  | 'communication'
  | 'storage'
  | 'crm'
  | 'analytics'
  | 'other';

// Campo de autenticação dinâmico
export interface AuthField {
  key: string;
  type: 'text' | 'password' | 'email' | 'number' | 'select';
  label: string;
  secret?: boolean;
  required?: boolean;
  options?: string[]; // Para campos select
  description?: string;
}

// Campo de configuração adicional
export interface FormField {
  key?: string;
  name: string;
  type: 'text' | 'password' | 'email' | 'number' | 'select' | 'checkbox';
  label: string;
  required?: boolean;
  description?: string;
  placeholder?: string;
  options?: string[]; // Para campos select
  default?: string | number | boolean;
  readonly?: boolean; // Se true, o campo é exibido mas não editável (ex: webhook_url computado)
}

// Estrutura do auth_fields
export interface AuthFieldsStructure {
  fields: AuthField[];
  headers: Record<string, string>;
  auth_type: 'bearer' | 'basic' | 'api_key' | 'oauth2';
}

// Estrutura do settings para TEMPLATE (com form_fields)
export interface SettingsStructure {
  description?: string | { en: string; pt: string }; // Descrição traduzível
  block?: boolean; // Se true, bloqueia edição/exclusão da integration_base
  form_fields: FormField[];
}

// Estrutura do settings para INSTÂNCIA (valores simples)
export interface SettingsInstance {
  [key: string]: unknown; // Campos dinâmicos com valores simples
}

// Template de integração (endpoint /integrations)
export interface Integration {
  id: string;
  name: string; // Nome técnico (ex: "gemini")
  label: string; // Nome amigável (ex: "Google Gemini")
  description: string | null;
  classification: IntegrationClassification;
  auth_type?: string; // Tipo de autenticação
  auth_fields: AuthFieldsStructure; // Formulário dinâmico de autenticação
  settings: SettingsStructure | null; // Configurações adicionais
  /**
   * Descrição técnica do template: `models` (por modelo: rota, api-version
   * e, quando divergir, provider e endpoint) e `cost_source`. Não é editável na tela —
   * a tela apenas SUGERE os modelos declarados aqui (AB#85434).
   */
  metadata?: Record<string, unknown> | null;
  logo_url: string | null;
  url: string | null;
  status: IntegrationStatus;
  active: boolean; // Indica se o template está ativo/disponível
  created_at: string;
  updated_at: string;
  organization_id: string;
}

// Instância de integração configurada (endpoint /integrations-base)
export interface IntegrationBase {
  id: string;
  name: string;
  label: string;
  title?: string; // Nome customizado pelo usuário (ex: "Jira — Mobile")
  description: string | null;
  classification: IntegrationClassification;
  organization_id: string;
  integration_id: string;
  auth_type?: string; // Tipo de autenticação copiado do template
  auth_fields: Record<string, unknown>;
  settings?: SettingsInstance | null; // Valores simples dos campos (não a estrutura do template)
  /** Copiado do template na instalação — ver `Integration.metadata` (AB#85434). */
  metadata?: Record<string, unknown> | null;
  logo_url: string | null; // Logo copiada do template
  url: string | null; // URL copiada do template
  status: IntegrationStatus;
  is_encrypted: boolean;
  last_validated_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  template?: Integration; // Incluído quando with_template=true
  scope?: 'local' | 'global'; // Persistido no banco
}

export interface IntegrationPostInstallEndpoint {
  key: string;
  label: { pt?: string; en?: string; [k: string]: string | undefined };
  description?: { pt?: string; en?: string; [k: string]: string | undefined };
  value: string;
}

export interface IntegrationPostInstallManifestAsset {
  key: string;
  filename: string;
  url: string;
  label?: { pt?: string; en?: string; [k: string]: string | undefined };
}

// Informações opcionais retornadas pelo backend após criar uma integration_base.
// O template sinaliza (via settings.post_install) que deseja que o frontend exiba
// um modal com endpoints, manifest e instruções de setup. Pode incluir:
//   - endpoints[]      → múltiplos URLs nomeados a configurar no sistema externo
//   - manifest         → JSON renderizado (ex: Teams App manifest)
//   - manifest_assets  → arquivos binários a serem zipados junto ao manifest
//   - webhook_url      → campo legado (pré-endpoints[]), mantido para retrocompatibilidade
export interface IntegrationPostInstall {
  webhook_url?: string;
  setup_instructions?: {
    pt?: string[];
    en?: string[];
    [key: string]: string[] | undefined;
  };
  endpoints?: IntegrationPostInstallEndpoint[];
  manifest?: Record<string, unknown>;
  manifest_assets?: IntegrationPostInstallManifestAsset[];
  /** When true, render the manifest JSON in a code block (default false). */
  show_manifest_preview?: boolean;
  /** Per-template UI chrome labels (i18n maps). Falls back to built-in i18n keys. */
  labels?: {
    title?: { pt?: string; en?: string; [k: string]: string | undefined };
    subtitle?: { pt?: string; en?: string; [k: string]: string | undefined };
    manifest?: { pt?: string; en?: string; [k: string]: string | undefined };
    instructions?: { pt?: string; en?: string; [k: string]: string | undefined };
    download_zip?: { pt?: string; en?: string; [k: string]: string | undefined };
    copy_manifest?: { pt?: string; en?: string; [k: string]: string | undefined };
    copy?: { pt?: string; en?: string; [k: string]: string | undefined };
    confirm?: { pt?: string; en?: string; [k: string]: string | undefined };
  };
}

export interface CreateIntegrationBaseResult {
  integration: IntegrationBase;
  post_install?: IntegrationPostInstall;
}

// Dados para criação de nova integração base
/**
 * Só o que `POST /integration-bases` realmente recebe. Ficaram de fora
 * `organization_id` e `created_by` (derivados do token), e `title`, `auth_type` e
 * `auth_fields` (derivados do template no servidor) — estavam no tipo, um deles
 * obrigatório, e nenhum era enviado.
 */
export interface CreateIntegrationBaseData {
  label: string;
  description?: string;
  integration_id: string;
  product_id?: string; // ID do produto para integração workspace-scoped (legacy e v2 derivam scope 'local' + vínculo)
  scope?: 'local' | 'global'; // v2: separa visibilidade entre workspace e organização
  status?: IntegrationStatus;
  mcp_config?: Record<string, unknown>; // Override opcional do template
  // Para criação transacional com credenciais
  credentials?: Record<string, unknown>;
  settings?: SettingsInstance; // Valores simples dos campos (não a estrutura do template)
}

// Dados para atualização de integração base
export interface UpdateIntegrationBaseData {
  label?: string;
  title?: string;
  description?: string;
  credentials?: Record<string, unknown>; // Credenciais atualizadas
  settings?: SettingsInstance; // Valores simples dos campos (não a estrutura do template)
  mcp_config?: Record<string, unknown>; // Override opcional do template
  auth_fields?: Record<string, unknown>;
  status?: IntegrationStatus;
  scope?: 'local' | 'global';
  product_id?: string;
}

// Erro de dependências ao tentar excluir integração
export interface IntegrationDependenciesError {
  success: false;
  message: string;
  error: 'INTEGRATION_HAS_DEPENDENCIES';
  dependencies: {
    organizations: number;
    agentIntegrations: number;
    secrets: number;
  };
  details: string;
}

// Erro customizado para tratamento no frontend
export class IntegrationDependencyError extends Error {
  public readonly dependencies: {
    organizations: number;
    agentIntegrations: number;
    secrets: number;
  };
  public readonly details: string;

  constructor(errorData: IntegrationDependenciesError) {
    super(errorData.message);
    this.name = 'IntegrationDependencyError';
    this.dependencies = errorData.dependencies;
    this.details = errorData.details;
  }
}

export { AUTH_FIELD_CONFIGS } from "@/features/Auth/types/auth-fields";
